import { useState, type KeyboardEvent } from 'react'
import type { CasesCopy, CaseStudyCopy, ModuleId } from './content'
import { AgentMock, BomMock, CatalogMock, DocsMock, ErpMock, GridMock, MonitorMock, PanelMock, PortalMock, SearchMock, SpecMock, TowerMock, WorksMock } from './Mockups'
import './cases.css'

const moduleMocks: Record<ModuleId, typeof ErpMock> = {
  erp: ErpMock,
  portal: PortalMock,
  catalog: CatalogMock,
  search: SearchMock,
  agent: AgentMock,
  'grid-model': GridMock,
  'grid-asset': TowerMock,
  'grid-monitor': MonitorMock,
  'grid-works': WorksMock,
  'panel-spec': SpecMock,
  'panel-scheme': PanelMock,
  'panel-docs': DocsMock,
  'panel-bom': BomMock,
}

export function CaseStudy({ study, index, copy, idPrefix = '' }: { study: CaseStudyCopy; index: number; copy: CasesCopy; idPrefix?: string }) {
  const [active, setActive] = useState<ModuleId>(study.modules[0].id)
  const current = study.modules.find((module) => module.id === active) ?? study.modules[0]
  const Mock = moduleMocks[current.id]
  const panelId = `${idPrefix}case-panel-${study.id}`

  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>, position: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const next = study.modules[(position + (event.key === 'ArrowRight' ? 1 : -1) + study.modules.length) % study.modules.length]
    setActive(next.id)
    document.getElementById(`${idPrefix}case-tab-${next.id}`)?.focus()
  }

  return (
    <article className={`case-study case-${study.id}${study.id === 'platform' ? '' : ' case-energy'}`} data-reveal>
      <header className="case-head">
        <span className="case-sector">0{index + 1} · {study.sector}</span>
        <h3>{study.title}</h3>
        <p>{study.lead}</p>
        {study.standard && <span className="case-standard">{study.standard}</span>}
      </header>

      <div className="case-shift">
        <div><span>{copy.labels.before}</span><p>{study.before}</p></div>
        <i aria-hidden="true">→</i>
        <div className="after"><span>{copy.labels.after}</span><p>{study.after}</p></div>
      </div>

      <div className="case-metrics">
        {study.metrics.map((metric) => <div key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}
      </div>

      <div className="case-modules">
        <div className="case-tabs" role="tablist" aria-label={`${copy.labels.modules}: ${study.title}`}>
          {study.modules.map((module, position) => (
            <button
              key={module.id}
              id={`${idPrefix}case-tab-${module.id}`}
              role="tab"
              aria-selected={module.id === active}
              aria-controls={panelId}
              tabIndex={module.id === active ? 0 : -1}
              className={module.id === active ? 'active' : ''}
              onClick={() => setActive(module.id)}
              onKeyDown={(event) => onTabKey(event, position)}
            >
              <span>0{position + 1}</span>{module.tab}
            </button>
          ))}
        </div>
        <div className="case-module" id={panelId} role="tabpanel" aria-labelledby={`${idPrefix}case-tab-${current.id}`} key={current.id}>
          <div className="case-module-copy">
            <h4>{current.title}</h4>
            <p>{current.lead}</p>
            <ul>{current.points.map((point) => <li key={point}>{point}</li>)}</ul>
          </div>
          <div className="case-module-mock"><Mock ui={copy.ui} /></div>
        </div>
      </div>

      {study.results && (
        <div className="case-results">
          <h4>{copy.labels.results}</h4>
          <div>{study.results.map((result) => <div key={result.title}><b>{result.title}</b><p>{result.text}</p></div>)}</div>
        </div>
      )}
    </article>
  )
}

export function CasesShowcase({ copy }: { copy: CasesCopy }) {
  return (
    <div className="cases">
      {[copy.flagship, ...copy.energy].map((study, index) => <CaseStudy study={study} index={index} copy={copy} key={study.id} />)}
      <p className="work-note" data-reveal>{copy.note}</p>
    </div>
  )
}
